/** Mietrechnungen und offene Forderungen (Screen laden/Forderungen, MVP): Rechnungen lückenlos, Status, PDF, bezahlt setzen, Storno; Abrechnungen mit Forderung. Mahnwesen/Online-Zahlung sind V2. */
import { useState } from "react";
import { Link } from "react-router";
import { Button, Dialog, EmptyState, Notice, Seitenzustand, StatusBadge, TextField } from "../ui";
import { Card, Grid, KpiTile, PageHeader, Select } from "../ui/Layout";
import { ApiFehler } from "../api/client";
import { formatEuro } from "@shared/geld";
import { formatTag, tagVon } from "@shared/zeit";
import { exportUrl, mietrechnungPdfUrl, useAbrechnungen, useMietrechnungAktion, useMietrechnungen } from "./geld-api";
import { mahnungPdfUrl, useMahnAktion, useMahnungen } from "./geld-v2-api";
import { formatDatumZeit } from "@shared/zeit";

const STATUS: Record<string, { badge: "wait" | "paid" | "neutral" | "attention"; text: string }> = {
  offen: { badge: "wait", text: "Offen" },
  verrechnet: { badge: "paid", text: "Verrechnet" },
  bezahlt: { badge: "paid", text: "Bezahlt" },
  storniert: { badge: "neutral", text: "Storniert" },
};

export function Forderungen() {
  const [filter, setFilter] = useState<"alle" | "offen" | "ueberfaellig" | "bezahlt">("alle");
  const [suche, setSuche] = useState("");
  const q = useMietrechnungen();
  const forderungen = useAbrechnungen();
  const aktion = useMietrechnungAktion();
  const [dialog, setDialog] = useState<null | { id: string; nummer: string; art: "bezahlt" | "storno" }>(null);
  const [zahlArt, setZahlArt] = useState<"bar" | "ueberweisung">("ueberweisung");
  const [grund, setGrund] = useState("");
  const [meldung, setMeldung] = useState<{ tone: "success" | "error"; text: string } | null>(null);
  const heute = tagVon();
  const alle = q.data?.rechnungen ?? [];
  const offen = alle.filter((r) => r.status === "offen");
  const ueberfaellig = offen.filter((r) => r.faelligAt < heute);
  const bezahltMonat = alle.filter((r) => r.status === "bezahlt" && r.bezahltAt?.slice(0, 7) === heute.slice(0, 7));
  const liste = alle.filter((r) => {
    if (filter === "offen" && r.status !== "offen") return false;
    if (filter === "ueberfaellig" && !(r.status === "offen" && r.faelligAt < heute)) return false;
    if (filter === "bezahlt" && r.status !== "bezahlt") return false;
    if (suche && !r.mieterName.toLowerCase().includes(suche.toLowerCase()) && !r.nummer.toLowerCase().includes(suche.toLowerCase())) return false;
    return true;
  });
  const abrForderungen = (forderungen.data?.abrechnungen ?? []).filter((a) => a.status === "forderung");
  const jahr = heute.slice(0, 4);

  return (
    <>
      <PageHeader kicker="Geld" title="Mietrechnungen und offene Forderungen" sub="Rechnungen entstehen automatisch je Mietperiode und werden mit der Abrechnung verrechnet" actions={<a href={exportUrl("mietrechnungen", `${jahr}-01-01`, `${jahr}-12-31`)} className="rg-btn rg-btn--secondary">Export für Steuerbüro (CSV)</a>} />
      {meldung ? <Notice tone={meldung.tone}>{meldung.text}</Notice> : null}
      <Grid>
        <KpiTile label="Offen" value={formatEuro(offen.reduce((a, r) => a + r.bruttoCent, 0))} note={`${offen.length} Rechnungen, werden verrechnet`} />
        <KpiTile label="Überfällig" value={formatEuro(ueberfaellig.reduce((a, r) => a + r.bruttoCent, 0))} note={`${ueberfaellig.length} Rechnungen über 14 Tage`} />
        <KpiTile label="Direkt bezahlt im Monat" value={formatEuro(bezahltMonat.reduce((a, r) => a + r.bruttoCent, 0))} note={`${bezahltMonat.length} Rechnungen`} />
        <KpiTile label="Forderungen aus Abrechnungen" value={formatEuro(abrForderungen.reduce((a, x) => a - (x.umsatzCent + x.rabatteCent + x.rueckgabenCent + x.provisionCent + x.mieteCent + x.uebertragCent + x.sonstigesCent), 0))} note={`${abrForderungen.length} Mieter mit negativem Ergebnis`} />
      </Grid>
      {abrForderungen.length ? (
        <Card title="Abrechnungen mit Forderung">
          <div className="sc-col">
            {abrForderungen.map((a) => (
              <div key={a.id} className="sc-row" style={{ justifyContent: "space-between", flexWrap: "wrap" }}>
                <span><Link to={`/laden/abrechnungen/${a.id}`}>{a.nummer}</Link> · {a.mieterName} · {formatTag(a.periodeVon)}–{formatTag(a.periodeBis)}</span>
                <StatusBadge status="attention">Forderung {formatEuro(-(a.umsatzCent + a.rabatteCent + a.rueckgabenCent + a.provisionCent + a.mieteCent + a.uebertragCent + a.sonstigesCent))}</StatusBadge>
              </div>
            ))}
          </div>
          <p className="sc-muted sc-small" style={{ marginTop: "var(--space-3)" }}>Der Betrag wird mit der nächsten Abrechnung verrechnet. Zahlt der Mieter direkt, bitte die Mietrechnung als bezahlt markieren. Offene Miete lässt sich auch per <Link to="/laden/sepa">Lastschrift</Link> einziehen.</p>
        </Card>
      ) : null}
      <MahnungenKarte />
      <div className="sc-toolbar" style={{ margin: "var(--space-4) 0" }}>
        {(
          [
            ["alle", "Alle"],
            ["offen", `Offen ${offen.length}`],
            ["ueberfaellig", `Überfällig ${ueberfaellig.length}`],
            ["bezahlt", "Bezahlt"],
          ] as [typeof filter, string][]
        ).map(([k, t]) => <button key={k} type="button" className="sc-pill" aria-pressed={filter === k} onClick={() => setFilter(k)}>{t}</button>)}
        <span className="sc-grow" />
        <TextField label="Suche" icon="search" value={suche} onChange={(e) => setSuche(e.target.value)} placeholder="Mieter oder RL-Nummer" />
      </div>
      <Seitenzustand query={q} lines={6} istLeer={(d) => d.rechnungen.length === 0} leer={<Card><EmptyState compact illustration="all-done" title="Noch keine Mietrechnung">Mit der ersten begonnenen Mietperiode entsteht die erste Rechnung.</EmptyState></Card>}>
        {() => (
          <Card flush>
            <div className="app-tabellen-scroll">
              <table className="rg-table">
                <thead><tr><th>Nummer</th><th>Mieter</th><th>Zeitraum</th><th className="is-num">Betrag</th><th>Fällig</th><th>Status</th><th>Bezahlt per</th><th /></tr></thead>
                <tbody>
                  {liste.map((r) => {
                    const st = STATUS[r.status]!;
                    const ueber = r.status === "offen" && r.faelligAt < heute;
                    return (
                      <tr key={r.id} className={ueber ? "app-zeile--offen" : undefined}>
                        <td className="app-code">{r.nummer}{r.stornoVonId ? <div className="sc-muted sc-small">Gegenrechnung</div> : null}</td>
                        <td><Link to={`/laden/mieter/${r.mieterId}`}>{r.mieterName}</Link></td>
                        <td>{formatTag(r.leistungVon)}–{formatTag(r.leistungBis)}</td>
                        <td className="is-num">{formatEuro(r.bruttoCent)}</td>
                        <td>{formatTag(r.faelligAt)}</td>
                        <td><StatusBadge status={ueber ? "attention" : st.badge}>{ueber ? "Überfällig" : st.text}</StatusBadge></td>
                        <td>{r.bezahltArt === "bar" ? "Bar" : r.bezahltArt === "ueberweisung" ? "Überweisung" : r.bezahltArt === "lastschrift" ? "Lastschrift" : r.status === "verrechnet" ? "Abrechnung" : "–"}</td>
                        <td>
                          <span className="sc-toolbar">
                            <a href={mietrechnungPdfUrl(r.id)} target="_blank" rel="noreferrer" className="rg-btn rg-btn--quiet">PDF</a>
                            {r.status === "offen" ? <Button variant="quiet" icon="check" onClick={() => setDialog({ id: r.id, nummer: r.nummer, art: "bezahlt" })}>Bezahlt</Button> : null}
                            {r.status === "offen" || r.status === "bezahlt" ? <Button variant="quiet" icon="x" onClick={() => { setGrund(""); setDialog({ id: r.id, nummer: r.nummer, art: "storno" }); }}>Storno</Button> : null}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="sc-muted sc-small" style={{ padding: "var(--space-3) var(--space-4)" }}>{liste.length} von {alle.length} Rechnungen · Nummern fortlaufend, Storno als Gegenrechnung mit eigener Nummer.</p>
          </Card>
        )}
      </Seitenzustand>
      <Dialog open={!!dialog} title={dialog?.art === "bezahlt" ? `${dialog.nummer} als bezahlt markieren` : `${dialog?.nummer ?? ""} stornieren`} size="sm" onClose={() => setDialog(null)} actions={<><Button variant="quiet" onClick={() => setDialog(null)}>Abbrechen</Button><Button variant={dialog?.art === "storno" ? "danger" : "primary"} loading={aktion.isPending} onClick={() => dialog && aktion.mutate({ id: dialog.id, aktion: dialog.art, art: zahlArt, grund }, { onSuccess: () => { setDialog(null); setMeldung({ tone: "success", text: dialog.art === "bezahlt" ? "Als bezahlt markiert." : "Storniert – Gegenrechnung erstellt, die Periode wird neu berechnet." }); }, onError: (e) => setMeldung({ tone: "error", text: e instanceof ApiFehler ? e.message : "Das hat nicht geklappt." }) })}>{dialog?.art === "bezahlt" ? "Bezahlt" : "Stornieren"}</Button></>}>
        <div className="app-form">
          {dialog?.art === "bezahlt" ? (
            <Select label="Bezahlt per" value={zahlArt} onChange={(e) => setZahlArt(e.target.value as "bar" | "ueberweisung")} hint="Bar landet im Kassenbuch; das Mieterkonto wird entlastet">
              <option value="ueberweisung">Überweisung</option>
              <option value="bar">Bar im Laden</option>
            </Select>
          ) : (
            <TextField label="Grund" value={grund} onChange={(e) => setGrund(e.target.value)} autoFocus />
          )}
        </div>
      </Dialog>
    </>
  );
}

/** Mahnwesen (E9): letzte Mahnungen, Lauf von Hand, Storno mit Grund. Der tägliche Lauf kommt vom Cron. */
function MahnungenKarte() {
  const q = useMahnungen();
  const aktion = useMahnAktion();
  const [meldung, setMeldung] = useState<string | null>(null);
  const [offen, setOffen] = useState(false);
  const liste = (q.data?.mahnungen ?? []).slice(0, offen ? 200 : 8);
  const STUFE = ["", "Erinnerung", "Mahnung", "Letzte Mahnung"];
  return (
    <Card title={`Mahnungen · ${q.data?.mahnungen.filter((m) => !m.stornierAt).length ?? 0}`} actions={<span className="sc-toolbar"><Link to="/laden/einstellungen/abrechnung" className="rg-btn rg-btn--quiet">Einstellungen</Link><Button variant="secondary" icon="mail" loading={aktion.isPending && aktion.variables?.art === "lauf"} onClick={() => aktion.mutate({ art: "lauf" }, { onSuccess: (r) => setMeldung(`${(r as { neu: number }).neu} neue Mahnungen.`), onError: (e) => setMeldung(e instanceof ApiFehler ? e.message : "Lauf hat nicht geklappt.") })}>Mahnlauf jetzt</Button></span>}>
      {meldung ? <Notice tone="info">{meldung}</Notice> : null}
      <p className="sc-muted sc-small">Läuft täglich um 3 Uhr, wenn unter Einstellungen aktiviert: Erinnerung, Mahnung mit Gebühr, letzte Mahnung mit Einbehalt der Auszahlung. Gebühren sind eigene Rechnungen.</p>
      {liste.length ? (
        <div className="app-tabellen-scroll">
          <table className="rg-table">
            <thead><tr><th>Datum</th><th>Stufe</th><th>Rechnung</th><th>Mieter</th><th className="is-num">Offen</th><th className="is-num">Gebühr</th><th>Status</th><th /></tr></thead>
            <tbody>
              {liste.map((m) => (
                <tr key={m.id}>
                  <td>{formatDatumZeit(m.erstelltAt)}</td>
                  <td>{STUFE[m.stufe]}</td>
                  <td className="app-code">{m.rechnungNummer}</td>
                  <td><Link to={`/laden/mieter/${m.mieterId}`}>{m.mieterName}</Link></td>
                  <td className="is-num">{formatEuro(m.bruttoCent)}</td>
                  <td className="is-num">{m.gebuehrCent ? formatEuro(m.gebuehrCent) : "–"}</td>
                  <td>{m.stornierAt ? <StatusBadge status="neutral">Storniert</StatusBadge> : m.rechnungStatus === "offen" ? <StatusBadge status="attention">Offen</StatusBadge> : <StatusBadge status="paid">{m.rechnungStatus === "bezahlt" ? "Bezahlt" : "Erledigt"}</StatusBadge>}{m.versendetAt ? null : <span className="sc-muted sc-small"> · nicht gemailt</span>}</td>
                  <td><span className="sc-toolbar"><a href={mahnungPdfUrl(m.id)} target="_blank" rel="noreferrer" className="rg-btn rg-btn--quiet">PDF</a>{!m.stornierAt ? <Button variant="quiet" icon="x" onClick={() => { const grund = prompt("Grund für den Storno (z. B. Kulanz, schon bezahlt):") ?? ""; if (grund) aktion.mutate({ art: "storno", id: m.id, grund }); }}>Storno</Button> : null}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <p className="sc-muted" style={{ margin: 0 }}>Noch keine Mahnung.</p>}
      {(q.data?.mahnungen.length ?? 0) > 8 ? <Button variant="quiet" onClick={() => setOffen(!offen)}>{offen ? "Weniger" : "Alle anzeigen"}</Button> : null}
    </Card>
  );
}
